import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Loader2, Phone, ArrowRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function ScriptPicker() {
  const navigate = useNavigate();
  const [scripts, setScripts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [creating, setCreating] = useState(false);
  const [prospect, setProspect] = useState({ name: '', phone: '', company: '', revenue: '' });

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/triage/scripts`);
        setScripts(res.data.scripts || []);
      } catch (e) {
        toast.error('Failed to load triage scripts');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const startCall = async () => {
    if (!selected) return;
    if (!selected.is_ready) {
      toast.error('That script is not ready yet.');
      return;
    }
    setCreating(true);
    try {
      const res = await axios.post(`${API}/triage/calls`, {
        script_id: selected.script_id,
        trade: selected.trade,
        prospect_name: prospect.name || null,
        prospect_phone: prospect.phone || null,
        prospect_company: prospect.company || null,
        prospect_revenue_range: prospect.revenue || null,
      });
      navigate(`/triage/${res.data.call_id}`);
    } catch (e) {
      toast.error('Failed to start call');
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-white/40">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto">
      <h2 className="text-sm font-semibold tracking-wider text-white/40 uppercase mb-3">Choose a Script</h2>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
        {scripts.map((s) => {
          const isSel = selected?.script_id === s.script_id;
          return (
            <button
              key={s.script_id}
              disabled={!s.is_ready}
              onClick={() => setSelected(s)}
              className={`text-left p-4 rounded-xl border transition-all ${
                isSel
                  ? 'bg-[#00d4aa]/20 border-[#00d4aa]/40'
                  : s.is_ready
                  ? 'bg-white/5 border-white/10 hover:border-white/20'
                  : 'bg-white/5 border-white/5 opacity-40 cursor-not-allowed'
              }`}
            >
              <div className="flex items-center gap-2 mb-1">
                <Phone className="w-4 h-4 text-[#00d4aa]" />
                <span className="text-sm font-medium text-white">{s.display_name}</span>
              </div>
              <span className="text-[10px] text-white/40 uppercase tracking-wide">
                {s.is_icp ? 'ICP' : 'Catch-all'}{!s.is_ready ? ' · not ready' : ''}
              </span>
            </button>
          );
        })}
      </div>

      {selected && (
        <Card className="bg-white/5 border-white/10 mb-6">
          <CardContent className="p-4 grid grid-cols-2 gap-3">
            <Input placeholder="Prospect name" value={prospect.name}
              onChange={(e) => setProspect({ ...prospect, name: e.target.value })}
              className="bg-black/30 border-white/10 text-white" />
            <Input placeholder="Phone" value={prospect.phone}
              onChange={(e) => setProspect({ ...prospect, phone: e.target.value })}
              className="bg-black/30 border-white/10 text-white" />
            <Input placeholder="Company" value={prospect.company}
              onChange={(e) => setProspect({ ...prospect, company: e.target.value })}
              className="bg-black/30 border-white/10 text-white" />
            <Input placeholder="Revenue range (e.g. $150k-$500k)" value={prospect.revenue}
              onChange={(e) => setProspect({ ...prospect, revenue: e.target.value })}
              className="bg-black/30 border-white/10 text-white" />
          </CardContent>
        </Card>
      )}

      <Button
        onClick={startCall}
        disabled={!selected || !selected.is_ready || creating}
        className="bg-[#00d4aa] hover:bg-[#00d4aa]/90 text-black font-medium"
      >
        {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ArrowRight className="w-4 h-4 mr-2" />}
        Start Triage Call
      </Button>
    </div>
  );
}

export default ScriptPicker;
